import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { StrainQuery, StrainSummary } from '@shared';
import { useRevokeObjectUrls } from '../hooks/useRevokeObjectUrls';
import {
  getProductRepository,
  type CatalogueMeta,
} from '../lib/productRepository';
import { revokeDisplayObjectUrls } from '../lib/productImages/objectUrls';
import { StrainCard } from '../components/StrainCard';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PrimaryLink,
  SearchBar,
} from '../components/ui';
import { useUserProfile } from '../hooks/useUserProfile';
import { getProfileGreeting } from '../lib/greeting';

function summaryBlobUrls(rows: StrainSummary[]): Array<string | null> {
  return rows.flatMap((s) => [
    s.localPrimaryThumbnailUrl,
    s.firstLocalThumbnailUrl,
    s.primaryThumbnailUrl,
  ]);
}

/**
 * Library — active spreadsheet + local products with search.
 */
export function Catalogue(): JSX.Element {
  const { profile } = useUserProfile();
  const [query, setQuery] = useState<Partial<StrainQuery>>({
    sort: 'recent',
    archived: 'active',
    verified: 'all',
  });
  const [strains, setStrains] = useState<StrainSummary[]>([]);
  const [meta, setMeta] = useState<CatalogueMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getProductRepository()
      .meta()
      .then(setMeta)
      .catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProductRepository()
      .list(query)
      .then((rows) => {
        if (cancelled) {
          // Abandoned hydration still created blob: URLs — revoke them.
          revokeDisplayObjectUrls(summaryBlobUrls(rows));
          return;
        }
        setStrains(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Could not load library.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  useRevokeObjectUrls(summaryBlobUrls(strains));

  const update = <K extends keyof StrainQuery>(
    key: K,
    value: StrainQuery[K],
  ): void => setQuery((q) => ({ ...q, [key]: value }));

  return (
    <div className="library-page">
      <header className="library-header">
        <div className="library-header__title-row">
          <h1 className="library-title">
            {profile ? getProfileGreeting(profile.displayName) : 'Library'}
          </h1>
          <PrimaryLink
            to="/add"
            icon={<Plus size={16} strokeWidth={2.25} />}
            className="library-add-btn"
          >
            Add Product
          </PrimaryLink>
        </div>
        <p className="library-subtitle">What are you in the mood for?</p>
        {meta && meta.total > 0 ? (
          <span className="visually-hidden">{meta.total} products in library</span>
        ) : null}
      </header>

      <div className="library-search-row">
        <SearchBar
          value={query.search ?? ''}
          onChange={(v) => update('search', v)}
          placeholder="Search name, brand, Product ID…"
        />
      </div>

      {loading && <LoadingState label="Loading library…" />}
      {error && <ErrorState message={error} />}

      {!loading && !error && strains.length === 0 && (
        <EmptyState
          title="No products match"
          description="Try another search, pull from the spreadsheet, or add a local product."
          action={
            <PrimaryLink to="/add" icon={<Plus size={18} />}>
              Add Product
            </PrimaryLink>
          }
        />
      )}

      <div className="library-list">
        {strains.map((s) => (
          <StrainCard key={s.id} strain={s} />
        ))}
      </div>
    </div>
  );
}
